/**
 * Phone menu (IVR) editor — every Agent9 login and every TeleSales company.
 * The company writes its own words; Ava reads exactly those words, then
 * forwards a key to that department's station number. Off until saved on.
 * A save applies to the very next call.
 */
import { useEffect, useMemo, useRef, useState } from 'react';
import { toast } from 'sonner';
import { Plus, Trash2, Volume2, Save, PhoneForwarded } from 'lucide-react';
import { Button } from './ui/button';
import { Input } from './ui/input';
import { Textarea } from './ui/textarea';
import { Switch } from './ui/switch';

const KEYS = ['1', '2', '3', '4', '5', '6', '7', '8', '9'];
const DEFAULT_OPERATOR = 'To speak with our receptionist, press 0 or stay on the line.';

function nextKey(options) {
  const used = new Set(options.map(o => o.key));
  return KEYS.find(k => !used.has(k)) || '';
}

export default function PhoneMenuEditor({ load, save, preview, numberLabel }) {
  const [menu, setMenu] = useState(null);
  const [errors, setErrors] = useState([]);
  const [busy, setBusy] = useState(false);
  const [hearing, setHearing] = useState(false);
  const [savedAt, setSavedAt] = useState('');
  const audioRef = useRef(null);

  useEffect(() => {
    let alive = true;
    load().then(m => {
      if (!alive) return;
      setMenu({ enabled: !!m.enabled, welcome: m.welcome || '', operator_say: m.operator_say || DEFAULT_OPERATOR,
        options: (m.options || []).map(o => ({ ...o })) });
      setSavedAt(m.updated_at || '');
    }).catch(e => toast.error(e.message || 'Could not load the phone menu'));
    return () => { alive = false; };
  }, [load]);

  const script = useMemo(() => {
    if (!menu) return '';
    const parts = [menu.welcome, ...menu.options.map(o => o.say || (o.label && o.key ? `For ${o.label}, press ${o.key}.` : '')),
      menu.operator_say || DEFAULT_OPERATOR];
    return parts.map(p => (p || '').trim()).filter(Boolean).join(' ');
  }, [menu]);

  if (!menu) return <div className="text-sm text-muted-foreground p-6">Loading phone menu…</div>;

  const set = (k, v) => setMenu({ ...menu, [k]: v });
  const setRow = (i, k, v) => set('options', menu.options.map((o, j) => (j === i ? { ...o, [k]: v } : o)));
  const addRow = () => {
    const key = nextKey(menu.options);
    if (!key) return;
    set('options', [...menu.options, { key, label: '', say: '', number: '' }]);
  };
  const removeRow = (i) => set('options', menu.options.filter((_, j) => j !== i));

  const doSave = async () => {
    setBusy(true);
    setErrors([]);
    try {
      const res = await save(menu);
      setMenu({ ...menu, options: res.options || menu.options });
      setSavedAt(res.updated_at || new Date().toISOString());
      toast.success(menu.enabled ? 'Phone menu saved. It plays on the next call.' : 'Saved. The phone menu is off.');
    } catch (e) {
      const list = e.errors || (e.message ? [e.message] : ['Could not save']);
      setErrors(list);
      toast.error('Please fix the highlighted items.');
    } finally { setBusy(false); }
  };

  const doHear = async () => {
    setHearing(true);
    try {
      const res = await preview(menu);
      if (res.audio_url && audioRef.current) {
        audioRef.current.src = res.audio_url;
        await audioRef.current.play().catch(() => {});
      } else {
        toast.message('Voice preview is not available right now. Callers will hear these words.');
      }
    } catch (e) { toast.error(e.message || 'Preview failed'); } finally { setHearing(false); }
  };

  return (
    <div className="space-y-6" data-testid="phone-menu-editor">
      <div className="flex items-start justify-between gap-4 rounded-xl border p-4">
        <div>
          <p className="font-medium text-foreground">Phone menu</p>
          <p className="text-sm text-muted-foreground">
            When on, callers to {numberLabel || 'your number'} hear your menu first. A key forwards them to that
            department; 0 or no key goes to your AI receptionist.
          </p>
        </div>
        <Switch checked={menu.enabled} onCheckedChange={(v) => set('enabled', v)} data-testid="phone-menu-enabled" />
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="pm-welcome">Welcome words (the AI reads exactly this)</label>
        <Textarea id="pm-welcome" rows={2} maxLength={400} value={menu.welcome} data-testid="phone-menu-welcome"
          placeholder="Thank you for calling our company." onChange={(e) => set('welcome', e.target.value)} />
      </div>

      <div className="space-y-3">
        <p className="text-sm font-medium">Departments</p>
        {menu.options.length === 0 && (
          <p className="text-sm text-muted-foreground">No departments yet. Add one: a key, its name, what the AI says,
            and the station number to forward to.</p>
        )}
        {menu.options.map((o, i) => (
          <div key={i} className="grid gap-2 rounded-xl border p-3 md:grid-cols-[80px_1fr_2fr_1fr_auto] md:items-end"
            data-testid={`phone-menu-row-${i}`}>
            <div>
              <label className="text-xs text-muted-foreground">Press</label>
              <select className="h-9 w-full rounded-md border bg-background px-2 text-sm" value={o.key}
                data-testid={`phone-menu-key-${i}`} onChange={(e) => setRow(i, 'key', e.target.value)}>
                {KEYS.map(k => <option key={k} value={k}>{k}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Department</label>
              <Input value={o.label} maxLength={40} placeholder="Technical" data-testid={`phone-menu-label-${i}`}
                onChange={(e) => setRow(i, 'label', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">What the AI says</label>
              <Input value={o.say} maxLength={300} data-testid={`phone-menu-say-${i}`}
                placeholder={o.label ? `For ${o.label}, press ${o.key}.` : 'For technical support, press 1.'}
                onChange={(e) => setRow(i, 'say', e.target.value)} />
            </div>
            <div>
              <label className="text-xs text-muted-foreground">Station / forward number</label>
              <Input value={o.number} inputMode="tel" placeholder="(209) 555-0101" data-testid={`phone-menu-number-${i}`}
                onChange={(e) => setRow(i, 'number', e.target.value)} />
            </div>
            <Button type="button" variant="ghost" size="icon" aria-label="Remove department"
              data-testid={`phone-menu-remove-${i}`} onClick={() => removeRow(i)}>
              <Trash2 className="h-4 w-4" />
            </Button>
          </div>
        ))}
        <Button type="button" variant="outline" size="sm" onClick={addRow} disabled={menu.options.length >= 9}
          data-testid="phone-menu-add">
          <Plus className="mr-1 h-4 w-4" /> Add department
        </Button>
      </div>

      <div className="space-y-2">
        <label className="text-sm font-medium" htmlFor="pm-operator">Press 0: your AI receptionist</label>
        <Input id="pm-operator" maxLength={300} value={menu.operator_say} data-testid="phone-menu-operator"
          onChange={(e) => set('operator_say', e.target.value)} />
      </div>

      <div className="rounded-xl bg-muted/50 p-4 text-sm" data-testid="phone-menu-script">
        <p className="mb-1 flex items-center gap-2 font-medium"><PhoneForwarded className="h-4 w-4" /> Callers will hear</p>
        <p className="text-muted-foreground">{script || 'Add your words above.'}</p>
      </div>

      {errors.length > 0 && (
        <ul className="list-disc space-y-1 rounded-xl border border-destructive/40 bg-destructive/5 p-4 pl-8 text-sm text-destructive"
          data-testid="phone-menu-errors">
          {errors.map((e, i) => <li key={i}>{e}</li>)}
        </ul>
      )}

      <div className="flex flex-wrap items-center gap-3">
        <Button onClick={doSave} disabled={busy} data-testid="phone-menu-save">
          <Save className="mr-1 h-4 w-4" /> {busy ? 'Saving…' : 'Save'}
        </Button>
        <Button variant="outline" onClick={doHear} disabled={hearing || !script} data-testid="phone-menu-hear">
          <Volume2 className="mr-1 h-4 w-4" /> {hearing ? 'Loading…' : 'Hear it'}
        </Button>
        {savedAt && <span className="text-xs text-muted-foreground" data-testid="phone-menu-saved">
          Saved {new Date(savedAt).toLocaleString()}</span>}
        <audio ref={audioRef} className="hidden" />
      </div>
    </div>
  );
}
